import React, { useRef, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Eye, Loader2, Trash2 } from 'lucide-react';
import './FilterModal.scss';

interface FilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  resultCount?: number | null;
  countLoading?: boolean;
  /** When true, shows a small banner indicating option counts are being computed. */
  facetsLoading?: boolean;
  onShowResults?: () => void;
  onClearAll?: () => void;
  countLabel?: 'jobs' | 'candidates';
  /** Footer primary button. Defaults to “Rodyti rezultatus”. */
  actionLabel?: string;
}

const FilterModal: React.FC<FilterModalProps> = ({
  isOpen,
  onClose,
  title,
  icon,
  children,
  resultCount,
  countLoading,
  facetsLoading,
  onShowResults,
  onClearAll,
  countLabel = 'jobs',
  actionLabel = 'Rodyti rezultatus',
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const mouseDownTarget = useRef<EventTarget | null>(null);

  const handleOverlayMouseDown = useCallback((e: React.MouseEvent) => {
    // Only track if clicking directly on the overlay
    if (e.target === e.currentTarget) {
      mouseDownTarget.current = e.target;
    }
  }, []);

  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    // Only close if:
    // 1. Not currently dragging inside modal
    // 2. Click started on overlay
    // 3. Click ended on overlay
    if (!isDragging && mouseDownTarget.current === e.target && e.target === e.currentTarget) {
      onClose();
    }
    mouseDownTarget.current = null;
  }, [onClose, isDragging]);

  const handleModalMouseDown = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleModalMouseUp = useCallback(() => {
    // Delay resetting to allow click event to complete
    setTimeout(() => setIsDragging(false), 100);
  }, []);

  const handleShowResults = () => {
    if (onShowResults) {
      onShowResults();
    }
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <>
      <div 
        className="filter-modal-overlay" 
        onMouseDown={handleOverlayMouseDown}
        onClick={handleOverlayClick}
      />
      <div 
        className="filter-modal"
        onMouseDown={handleModalMouseDown}
        onMouseUp={handleModalMouseUp}
        onMouseLeave={handleModalMouseUp}
      >
        <div className="filter-modal-header">
          <div className="modal-title">
            {icon}
            <h2>{title}</h2>
          </div>
          <button className="close-btn" onClick={onClose}>
            <X size={24} />
          </button>
        </div>
        <div className="filter-modal-content">
          {facetsLoading && (
            <div className="facets-loading-bar">
              <div className="facets-loading-progress" />
              <span className="facets-loading-label">
                <Loader2 size={15} className="spin" />
                Skaičiuojamos parinktys...
              </span>
            </div>
          )}
          <div className={`filter-modal-options${facetsLoading ? ' is-loading' : ''}`}>
            {children}
          </div>
        </div>
        
        {/* Footer with live count and action buttons */}
        <div className="filter-modal-footer">
          <div className="result-count">
            {countLoading ? (
              <span className="count-loading">
                <Loader2 size={16} className="spin" />
                Skaičiuojama...
              </span>
            ) : resultCount !== null && resultCount !== undefined ? (
              <span className={`count ${resultCount === 0 ? 'no-results' : ''}`}>
                {resultCount === 0 ? (
                  'Rezultatų nerasta'
                ) : countLabel === 'candidates' ? (
                  `Rasta: ${resultCount} kandidat${resultCount === 1 ? 'as' : resultCount < 10 || resultCount > 20 ? (resultCount % 10 === 1 ? 'as' : resultCount % 10 >= 2 && resultCount % 10 <= 9 ? 'ai' : 'ų') : 'ų'}`
                ) : (
                  `Rasta: ${resultCount} skelbim${resultCount === 1 ? 'as' : resultCount < 10 || resultCount > 20 ? (resultCount % 10 === 1 ? 'as' : resultCount % 10 >= 2 && resultCount % 10 <= 9 ? 'ai' : 'ų') : 'ų'}`
                )}
              </span>
            ) : null}
          </div>
          <div className="footer-actions">
            {onClearAll && (
              <button className="clear-modal-btn" onClick={onClearAll}>
                <Trash2 size={15} />
                Ištrinti visus
              </button>
            )}
            <button
              className="show-results-btn"
              onClick={handleShowResults}
              disabled={resultCount === 0}
            >
              <Eye size={18} />
              {actionLabel}
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default FilterModal;

